import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import CaseType from '../../entities/caseType';
import Matter from '../../entities/matter';
import CaseTypeStage from '../../entities/caseTypeStage';
import DeadlineRule from '../../entities/deadlineRule';
import Lead from '../../entities/lead';
import Question from '../../entities/question';
import RedFlagType from '../../entities/redFlagType';

/*
 * Delete/nullify anywhere this caseType is used in other entities
 *
 * @param {CaseType} caseType
 */
export function deleteCaseType(caseType: CaseType, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = caseType.id;
    }
    /* Simple delete usages */
        
    store.getState().matters
        .getMappingFor('caseTypeId', caseType.id)
        .filter(matterTemp => matterTemp.status !== 'D')
        .forEach(matterTemp => {
            const _matter = matterTemp.clone();
            _matter.status =  'U';
            _matter.addedOn = newHRTimestamp();
            _matter.addedBy = getCurrentUserId();
            _matter.importSource = targetImportSource;
            _matter.importId = mergeId;

            _matter.caseTypeId = null;
            store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
        });

    store.getState().caseTypeStages
        .getMappingFor('caseTypeId', caseType.id)
        .filter(caseTypeStageTemp => caseTypeStageTemp.status !== 'D')
        .forEach(caseTypeStageTemp => {
            const _caseTypeStage = caseTypeStageTemp.clone();
            _caseTypeStage.status =  'U';
            _caseTypeStage.addedOn = newHRTimestamp();
            _caseTypeStage.addedBy = getCurrentUserId();
            _caseTypeStage.importSource = targetImportSource;
            _caseTypeStage.importId = mergeId;

            _caseTypeStage.caseTypeId = '';
            store.dispatch(Actions.CaseTypeStageActions.saveCaseTypeStageQueue(_caseTypeStage));
        });

    store.getState().deadlineRules
        .getMappingFor('caseTypeId', caseType.id)
        .filter(deadlineRuleTemp => deadlineRuleTemp.status !== 'D')
        .forEach(deadlineRuleTemp => {
            const _deadlineRule = deadlineRuleTemp.clone();
            _deadlineRule.status =  'U';
            _deadlineRule.addedOn = newHRTimestamp();
            _deadlineRule.addedBy = getCurrentUserId();
            _deadlineRule.importSource = targetImportSource;
            _deadlineRule.importId = mergeId;

            _deadlineRule.caseTypeId = null;
            store.dispatch(Actions.DeadlineRuleActions.saveDeadlineRuleQueue(_deadlineRule));
        });

    store.getState().leads
        .getMappingFor('caseTypeId', caseType.id)
        .filter(leadTemp => leadTemp.status !== 'D')
        .forEach(leadTemp => {
            const _lead = leadTemp.clone();
            _lead.status =  'U';
            _lead.addedOn = newHRTimestamp();
            _lead.importSource = targetImportSource;
            _lead.importId = mergeId;

            _lead.caseTypes = _lead.caseTypes?.filter(id => id !== caseType.id) || [];
            store.dispatch(Actions.LeadActions.saveLeadQueue(_lead));
        });

    store.getState().questions
        .getMappingFor('caseTypeId', caseType.id)
        .filter(questionTemp => questionTemp.status !== 'D')
        .forEach(questionTemp => {
            const _question = questionTemp.clone();
            _question.status =  'U';
            _question.addedOn = newHRTimestamp();
            _question.importSource = targetImportSource;
            _question.importId = mergeId;

            _question.appliesTo = _question.appliesTo?.filter(id => id !== caseType.id) || [];
            store.dispatch(Actions.QuestionActions.saveQuestionQueue(_question));
        });

    store.getState().redFlagTypes
        .getMappingFor('caseTypeId', caseType.id)
        .filter(redFlagTypeTemp => redFlagTypeTemp.status !== 'D')
        .forEach(redFlagTypeTemp => {
            const _redFlagType = redFlagTypeTemp.clone();
            _redFlagType.status =  'U';
            _redFlagType.addedOn = newHRTimestamp();
            _redFlagType.importSource = targetImportSource;
            _redFlagType.importId = mergeId;

            _redFlagType.appliesTo = _redFlagType.appliesTo?.filter(id => id !== caseType.id) || [];
            store.dispatch(Actions.RedFlagTypeActions.saveRedFlagTypeQueue(_redFlagType));
        });

    /* Remove the actual caseType */
    caseType.status = 'D';
    caseType.addedOn = newHRTimestamp();
    caseType.addedBy = getCurrentUserId();
    caseType.importSource = targetImportSource;
    caseType.importId = mergeId;
    store.dispatch(Actions.CaseTypeActions.removeCaseTypeQueue(caseType));
    
}

/*
 * Replace anywhere this caseType is used in other entities.
 *
 * @param {CaseType} caseType
 * @param {CaseType} replaceWith
 * @return {CaseType} - CaseType will have refs stripped away
 */
export function replaceCaseType(caseType: CaseType, replaceWith: CaseType, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = caseType.id;
    }
    /* Simple replace usages */
        
    store.getState().matters
        .getMappingFor('caseTypeId', caseType.id)
        .filter(matterTemp => matterTemp.status !== 'D')
        .forEach(matterTemp => {
            const _matter = matterTemp.clone();
            _matter.status =  'U';
            _matter.addedOn = newHRTimestamp();
            _matter.addedBy = getCurrentUserId();
            _matter.importSource = targetImportSource;
            _matter.importId = mergeId;

            _matter.caseTypeId = replaceWith.id;
            store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
        });

    store.getState().caseTypeStages
        .getMappingFor('caseTypeId', caseType.id)
        .filter(caseTypeStageTemp => caseTypeStageTemp.status !== 'D')
        .forEach(caseTypeStageTemp => {
            const _caseTypeStage = caseTypeStageTemp.clone();
            _caseTypeStage.status =  'U';
            _caseTypeStage.addedOn = newHRTimestamp();
            _caseTypeStage.addedBy = getCurrentUserId();
            _caseTypeStage.importSource = targetImportSource;
            _caseTypeStage.importId = mergeId;

            _caseTypeStage.caseTypeId = replaceWith.id;
            store.dispatch(Actions.CaseTypeStageActions.saveCaseTypeStageQueue(_caseTypeStage));
        });

    store.getState().deadlineRules
        .getMappingFor('caseTypeId', caseType.id)
        .filter(deadlineRuleTemp => deadlineRuleTemp.status !== 'D')
        .forEach(deadlineRuleTemp => {
            const _deadlineRule = deadlineRuleTemp.clone();
            _deadlineRule.status =  'U';
            _deadlineRule.addedOn = newHRTimestamp();
            _deadlineRule.addedBy = getCurrentUserId();
            _deadlineRule.importSource = targetImportSource;
            _deadlineRule.importId = mergeId;

            _deadlineRule.caseTypeId = replaceWith.id;
            store.dispatch(Actions.DeadlineRuleActions.saveDeadlineRuleQueue(_deadlineRule));
        });

    store.getState().leads
        .getMappingFor('caseTypeId', caseType.id)
        .filter(leadTemp => leadTemp.status !== 'D')
        .forEach(leadTemp => {
            const _lead = leadTemp.clone();
            _lead.status =  'U';
            _lead.addedOn = newHRTimestamp();
            _lead.importSource = targetImportSource;
            _lead.importId = mergeId;

            _lead.caseTypes = _lead.caseTypes?.map(id => (id === caseType.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.LeadActions.saveLeadQueue(_lead));
        });

    store.getState().questions
        .getMappingFor('caseTypeId', caseType.id)
        .filter(questionTemp => questionTemp.status !== 'D')
        .forEach(questionTemp => {
            const _question = questionTemp.clone();
            _question.status =  'U';
            _question.addedOn = newHRTimestamp();
            _question.importSource = targetImportSource;
            _question.importId = mergeId;

            _question.appliesTo = _question.appliesTo?.map(id => (id === caseType.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.QuestionActions.saveQuestionQueue(_question));
        });

    store.getState().redFlagTypes
        .getMappingFor('caseTypeId', caseType.id)
        .filter(redFlagTypeTemp => redFlagTypeTemp.status !== 'D')
        .forEach(redFlagTypeTemp => {
            const _redFlagType = redFlagTypeTemp.clone();
            _redFlagType.status =  'U';
            _redFlagType.addedOn = newHRTimestamp();
            _redFlagType.importSource = targetImportSource;
            _redFlagType.importId = mergeId;

            _redFlagType.appliesTo = _redFlagType.appliesTo?.map(id => (id === caseType.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.RedFlagTypeActions.saveRedFlagTypeQueue(_redFlagType));
        });

    return caseType;
}

/*
 * Determine the impact of deleting this caseType from other entities
 *
 * @param {CaseType} caseType
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactCaseType(caseType: CaseType) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const matterCaseTypeIdImpact = state.matters
          .getMappingFor('caseTypeId', caseType.id)
          .filter(matter => matter.status !== 'D');

    matterCaseTypeIdImpact.forEach(() => impacts.push('matters'));

    const caseTypeStageCaseTypeIdImpact = state.caseTypeStages
          .getMappingFor('caseTypeId', caseType.id)
          .filter(caseTypeStage => caseTypeStage.status !== 'D');

    caseTypeStageCaseTypeIdImpact.forEach(() => impacts.push('caseTypeStages*'));

    const deadlineRuleCaseTypeIdImpact = state.deadlineRules
          .getMappingFor('caseTypeId', caseType.id)
          .filter(deadlineRule => deadlineRule.status !== 'D');

    deadlineRuleCaseTypeIdImpact.forEach(() => impacts.push('deadlineRules'));

    const leadCaseTypesImpact = state.leads
          .getMappingFor('caseTypeId', caseType.id)
          .filter(lead => lead.status !== 'D');

    leadCaseTypesImpact.forEach(() => impacts.push('leads'));

    const questionAppliesToImpact = state.questions
          .getMappingFor('caseTypeId', caseType.id)
          .filter(question => question.status !== 'D');

    questionAppliesToImpact.forEach(() => impacts.push('questions'));

    const redFlagTypeAppliesToImpact = state.redFlagTypes
          .getMappingFor('caseTypeId', caseType.id)
          .filter(redFlagType => redFlagType.status !== 'D');

    redFlagTypeAppliesToImpact.forEach(() => impacts.push('redFlagTypes'));

    return util.countDistinct(impacts);
}